import styled from "styled-components";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  ACTIVITY_KINDS,
  ACTIVITY_TYPES,
  DATE_TIME_FORMAT,
  ACTIVITY_RESULT_TYPES,
  APPOINTMENT_CALL_RESULT_TYPES
} from "../strings";
import { generateDateTime } from "../../services/common-service";
import { formatBusiness } from "../../services/business-time";
import { useTranslation } from "react-i18next";

const ActivityDisplayLines = styled.div`
  line-height: 1.45;
`;

const ACTIVITY_RESTRICTED_CALL_NOTE =
  "Anruf nicht möglich, da der Lead aktuell nicht verfügbar ist.";

const LEGACY_RESTRICTED_CALL_NOTES = [
  "Restricted call, because lead is not available right now.",
  "Anruf eingeschränkt, da der Lead aktuell nicht verfügbar ist."
];

const CALL_ATTEMPT_ACTIVITY_KINDS = [
  ACTIVITY_KINDS.CALL_ATTEMPT,
  ACTIVITY_KINDS.APPOINTMENT_CALL_ATTEMPT
];

const CALL_ATTEMPT_TITLE_KEYS = {
  [ACTIVITY_KINDS.CALL_ATTEMPT]: "leadActivities.callAttempt.title",
  [ACTIVITY_KINDS.APPOINTMENT_CALL_ATTEMPT]:
    "leadActivities.callAttempt.appointmentTitle"
};

export const getNormalizedNote = (note = "") =>
  LEGACY_RESTRICTED_CALL_NOTES.includes(note)
    ? ACTIVITY_RESTRICTED_CALL_NOTE
    : note;

const hasStructuredActivityDisplay = (display) =>
  (display?.title || display?.titleKey) && Array.isArray(display?.lines);

const isCallAttemptActivity = (activity) =>
  CALL_ATTEMPT_ACTIVITY_KINDS.includes(activity.activityKind) &&
  activity.callAttempt;

export const hasActivityResultDisplay = (activity) =>
  isCallAttemptActivity(activity) ||
  hasStructuredActivityDisplay(activity.display);

const isAppointmentCallAttempt = (activity) =>
  activity.activityKind === ACTIVITY_KINDS.APPOINTMENT_CALL_ATTEMPT ||
  ((!activity.activityKind || activity.type === ACTIVITY_TYPES.APPOINTMENT) &&
    APPOINTMENT_CALL_RESULT_TYPES.includes(activity.result));

const getCallAttemptTitle = (activity, t) => {
  const titleKey = CALL_ATTEMPT_TITLE_KEYS[activity.activityKind];
  if (titleKey) {
    return t(titleKey);
  }
  return isAppointmentCallAttempt(activity)
    ? t("leadActivities.callAttempt.appointmentTitle")
    : t("leadActivities.callAttempt.title");
};

export const getTranslatedActivityResult = (t, result) =>
  result
    ? t(`leadActivities.resultType.${result}`, { defaultValue: result })
    : "---";

const getTranslatedNotInitiatedReason = (t, reason) =>
  reason
    ? t(`leadActivities.notInitiatedReason.${reason}`, {
        defaultValue: reason
      })
    : "";

const getDurationLabel = (t, durationSeconds) => {
  const duration = Number(durationSeconds);
  if (!Number.isFinite(duration) || duration <= 0) {
    return "";
  }

  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;
  if (!minutes) {
    return `${seconds} ${t("leadActivities.callAttempt.seconds")}`;
  }

  const minuteLabel =
    minutes === 1
      ? t("leadActivities.callAttempt.minute")
      : t("leadActivities.callAttempt.minutes");
  const minuteDuration = `${minutes} ${minuteLabel}`;
  const secondDuration = seconds
    ? `${seconds} ${t("leadActivities.callAttempt.seconds")}`
    : "";
  return [minuteDuration, secondDuration].filter(Boolean).join(" ");
};

const getCallAttemptLine = (label, value) =>
  value ? `${label}: ${value}` : null;

const getDisplayLineValue = (line, t) => {
  if (!line || typeof line === "string") {
    return line;
  }
  if (line.valueKey) {
    return t(line.valueKey, { defaultValue: line.value || line.valueKey });
  }
  if (line.valueType === "datetime") {
    return line.value ? generateDateTime(line.value, DATE_TIME_FORMAT) : "";
  }
  return line.value;
};

const getStructuredActivityDisplay = (display, t) => {
  if (!hasStructuredActivityDisplay(display)) {
    return null;
  }
  return {
    title: display.titleKey
      ? t(display.titleKey, { defaultValue: display.title || display.titleKey })
      : display.title,
    // Only the max-contact-attempt system activity gets this display.lines payload.
    lines: display.lines
      .map((line) => {
        if (!line || typeof line === "string") {
          return line;
        }
        const label = line.labelKey
          ? t(line.labelKey, { defaultValue: line.label || line.labelKey })
          : line.label;
        return getCallAttemptLine(label, getDisplayLineValue(line, t));
      })
      .filter(Boolean)
  };
};

const getCallAttemptDisplay = (activity, t, note) => {
  const {
    result,
    callAttempt: {
      attemptNumber,
      maxAttempts,
      startedAt,
      completedAt,
      durationSeconds,
      nextAttemptAt,
      notInitiatedReason
    } = {}
  } = activity;
  const reason =
    result === ACTIVITY_RESULT_TYPES.CALL_NOT_INITIATED
      ? getTranslatedNotInitiatedReason(t, notInitiatedReason)
      : "";
  const attemptLabel = attemptNumber
    ? [attemptNumber, maxAttempts].filter(Boolean).join("/")
    : "";

  return {
    title: getCallAttemptTitle(activity, t),
    lines: [
      getCallAttemptLine(
        t("leadActivities.callAttempt.result"),
        getTranslatedActivityResult(t, result)
      ),
      getCallAttemptLine(t("leadActivities.callAttempt.attempt"), attemptLabel),
      getCallAttemptLine(
        t("leadActivities.callAttempt.startedAt"),
        startedAt ? generateDateTime(startedAt, DATE_TIME_FORMAT) : ""
      ),
      getCallAttemptLine(
        t("leadActivities.callAttempt.completedAt"),
        completedAt ? generateDateTime(completedAt, DATE_TIME_FORMAT) : ""
      ),
      getCallAttemptLine(
        t("leadActivities.callAttempt.duration"),
        getDurationLabel(t, durationSeconds)
      ),
      getCallAttemptLine(
        t("leadActivities.callAttempt.nextAttemptAt"),
        // callback / availability times are German time on every PC
        nextAttemptAt ? formatBusiness(nextAttemptAt, DATE_TIME_FORMAT) : ""
      ),
      getCallAttemptLine(t("leadActivities.callAttempt.reason"), reason),
      getCallAttemptLine(t("leadActivities.noteTooltip"), note || "---")
    ].filter(Boolean)
  };
};

const ActivityResultDisplay = ({ activity, note }) => {
  const { t } = useTranslation();
  const { display } = activity;
  const callAttemptDisplay = isCallAttemptActivity(activity)
    ? getCallAttemptDisplay(activity, t, note)
    : null;
  const activityDisplay =
    callAttemptDisplay || getStructuredActivityDisplay(display, t);

  if (!activityDisplay) {
    return null;
  }

  const resultDisplay = (
    <div>
      <div className="fw-bold">{activityDisplay.title}</div>
      <ActivityDisplayLines>
        {activityDisplay.lines.map((line, lineIndex) => (
          <div key={`${line}-${lineIndex}`}>{line}</div>
        ))}
      </ActivityDisplayLines>
    </div>
  );

  if (callAttemptDisplay) {
    return resultDisplay;
  }

  return (
    <OverlayTrigger
      placement="left"
      overlay={<Tooltip>{t("common.result")}</Tooltip>}
    >
      {resultDisplay}
    </OverlayTrigger>
  );
};

export default ActivityResultDisplay;
